/**
 * Debug Overlay System
 *
 * A modular debug visualization system that allows adding custom debug widgets
 * Each widget is self-contained and can be added/removed dynamically
 */

export interface DebugWidget {
  name: string;
  container: HTMLElement;
  update(): void;
  destroy(): void;
}

export class DebugOverlay {
  private container!: HTMLElement;
  private contentContainer!: HTMLElement;
  private widgets: Map<string, DebugWidget> = new Map();
  private isVisible = false;
  private updateInterval: number | null = null;

  // Drag state
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  /** Tears down the fullscreen image inspector, if one is open. */
  private inspectorCleanup: (() => void) | null = null;

  constructor() {
    this.createOverlay();
  }

  private createOverlay(): void {
    // Create main container - positioned relative to render area
    this.container = document.createElement('div');
    this.container.className = 'potree-debug-overlay';
    this.container.style.cssText = `
      position: absolute;
      top: 60px;
      right: 10px;
      width: 400px;
      max-height: 80vh;
      background: rgba(0, 0, 0, 0.9);
      border: 1px solid #444;
      border-radius: 8px;
      color: #fff;
      font-family: monospace;
      font-size: 12px;
      z-index: 10000;
      display: none;
      overflow: hidden;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
      pointer-events: auto;
    `;

    // Create header
    const header = document.createElement('div');
    header.style.cssText = `
      padding: 12px 16px;
      background: rgba(255, 255, 255, 0.1);
      border-bottom: 1px solid #444;
      font-weight: bold;
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: grab;
      user-select: none;
    `;
    header.innerHTML = `
      <span>🐛 Debug Panel</span>
      <button class="potree-debug-close" style="
        background: none;
        border: none;
        color: #fff;
        cursor: pointer;
        font-size: 18px;
        padding: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
      ">×</button>
    `;

    const closeBtn = header.querySelector('.potree-debug-close') as HTMLElement;
    closeBtn.addEventListener('click', () => this.hide());

    // Setup drag functionality on header
    this.setupDrag(header);

    // Create scrollable content container
    this.contentContainer = document.createElement('div');
    this.contentContainer.style.cssText = `
      max-height: calc(80vh - 49px);
      overflow-y: auto;
      overflow-x: hidden;
      padding: 12px;
    `;

    // Add custom scrollbar styles
    const style = document.createElement('style');
    style.textContent = `
      .potree-debug-overlay::-webkit-scrollbar,
      .potree-debug-overlay > div::-webkit-scrollbar {
        width: 8px;
      }
      .potree-debug-overlay::-webkit-scrollbar-track,
      .potree-debug-overlay > div::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1);
      }
      .potree-debug-overlay::-webkit-scrollbar-thumb,
      .potree-debug-overlay > div::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 4px;
      }
      .potree-debug-overlay::-webkit-scrollbar-thumb:hover,
      .potree-debug-overlay > div::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.5);
      }
      .potree-debug-overlay canvas,
      .potree-debug-overlay img {
        cursor: zoom-in;
      }
    `;
    document.head.appendChild(style);

    // Any debug image is inspectable: click a canvas or img anywhere in
    // the panel to open it fullscreen, pixel-perfect, with zoom and pan.
    this.contentContainer.addEventListener('click', (e: MouseEvent) => {
      const target = e.target;
      if (target instanceof HTMLCanvasElement || target instanceof HTMLImageElement) {
        e.preventDefault();
        e.stopPropagation();
        this.openImageInspector(target);
      }
    });

    this.container.appendChild(header);
    this.container.appendChild(this.contentContainer);

    // Append to render area instead of body, so it moves with the render area
    // Wait for render area to be available
    const attachToRenderArea = () => {
      const renderArea = document.getElementById('potree_render_area');
      if (renderArea) {
        renderArea.appendChild(this.container);
      } else {
        // Retry if render area not found yet
        setTimeout(attachToRenderArea, 100);
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attachToRenderArea);
    } else {
      attachToRenderArea();
    }
  }

  /**
   * Setup drag functionality on the header element
   */
  private setupDrag(header: HTMLElement): void {
    const onMouseDown = (e: MouseEvent) => {
      // Don't start drag if clicking the close button
      if ((e.target as HTMLElement).classList.contains('potree-debug-close')) {
        return;
      }

      this.isDragging = true;
      header.style.cursor = 'grabbing';

      // Get current position (handle both 'auto' and pixel values)
      const rect = this.container.getBoundingClientRect();
      const parentRect = this.container.parentElement?.getBoundingClientRect() || { left: 0, top: 0 };

      // Calculate offset from mouse to container top-left corner
      this.dragOffsetX = e.clientX - rect.left;
      this.dragOffsetY = e.clientY - rect.top;

      // Store parent offset for relative positioning
      this.dragStartX = parentRect.left;
      this.dragStartY = parentRect.top;

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!this.isDragging) return;

      const parentRect = this.container.parentElement?.getBoundingClientRect() || { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };

      // Calculate new position relative to parent
      let newLeft = e.clientX - parentRect.left - this.dragOffsetX;
      let newTop = e.clientY - parentRect.top - this.dragOffsetY;

      // Constrain to parent bounds
      const containerRect = this.container.getBoundingClientRect();
      const maxLeft = parentRect.width - containerRect.width;
      const maxTop = parentRect.height - containerRect.height;

      newLeft = Math.max(0, Math.min(newLeft, maxLeft));
      newTop = Math.max(0, Math.min(newTop, maxTop));

      // Apply position (switch from right to left positioning)
      this.container.style.right = 'auto';
      this.container.style.left = `${newLeft}px`;
      this.container.style.top = `${newTop}px`;
    };

    const onMouseUp = () => {
      this.isDragging = false;
      header.style.cursor = 'grab';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    header.addEventListener('mousedown', onMouseDown);
  }

  /**
   * Open a fullscreen, pixel-perfect view of a debug image. The source is
   * snapshotted at its NATIVE resolution (canvas bitmap / natural image
   * size — not the CSS-scaled thumbnail) and rendered with
   * `image-rendering: pixelated`, so no interpolation ever touches the
   * pixels. Wheel zooms around the cursor, drag pans, double-click refits,
   * Escape / × / backdrop-click-without-drag closes.
   */
  private openImageInspector(source: HTMLCanvasElement | HTMLImageElement): void {
    this.closeImageInspector();
    const width = source instanceof HTMLCanvasElement ? source.width : source.naturalWidth;
    const height = source instanceof HTMLCanvasElement ? source.height : source.naturalHeight;
    if (!width || !height) return;

    // Snapshot: live canvases keep re-rendering; the inspector shows the
    // moment that was clicked.
    const snap = document.createElement('canvas');
    snap.width = width;
    snap.height = height;
    const ctx = snap.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(source, 0, 0);

    const backdrop = document.createElement('div');
    backdrop.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.95);
      z-index: 20000;
      overflow: hidden;
      cursor: grab;
      user-select: none;
    `;
    snap.style.cssText = `
      position: absolute;
      left: 0;
      top: 0;
      transform-origin: 0 0;
      image-rendering: pixelated;
    `;
    backdrop.appendChild(snap);

    const info = document.createElement('div');
    info.style.cssText = `
      position: absolute;
      left: 12px;
      bottom: 12px;
      padding: 4px 10px;
      background: rgba(0, 0, 0, 0.7);
      border: 1px solid #444;
      border-radius: 4px;
      color: #fff;
      font-family: monospace;
      font-size: 12px;
      pointer-events: none;
    `;
    backdrop.appendChild(info);

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.style.cssText = `
      position: absolute;
      top: 12px;
      right: 12px;
      width: 32px;
      height: 32px;
      background: rgba(0, 0, 0, 0.7);
      border: 1px solid #444;
      border-radius: 4px;
      color: #fff;
      font-size: 20px;
      cursor: pointer;
    `;
    backdrop.appendChild(closeBtn);

    let scale = 1;
    let tx = 0;
    let ty = 0;
    const apply = () => {
      snap.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
      info.textContent = `${width}×${height} px — ${Math.round(scale * 100)}%`;
    };
    const fit = () => {
      scale = Math.min(window.innerWidth / width, window.innerHeight / height);
      tx = (window.innerWidth - width * scale) / 2;
      ty = (window.innerHeight - height * scale) / 2;
      apply();
    };
    fit();

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const factor = e.deltaY < 0 ? 1.25 : 0.8;
      const next = Math.min(64, Math.max(0.02, scale * factor));
      // Keep the pixel under the cursor under the cursor.
      tx = e.clientX - ((e.clientX - tx) / scale) * next;
      ty = e.clientY - ((e.clientY - ty) / scale) * next;
      scale = next;
      apply();
    };

    let panning = false;
    let moved = false;
    let lastX = 0;
    let lastY = 0;
    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      panning = true;
      moved = false;
      lastX = e.clientX;
      lastY = e.clientY;
      backdrop.style.cursor = 'grabbing';
      e.preventDefault();
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!panning) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      if (Math.abs(dx) + Math.abs(dy) > 0) moved = true;
      lastX = e.clientX;
      lastY = e.clientY;
      tx += dx;
      ty += dy;
      apply();
    };
    const onMouseUp = (e: MouseEvent) => {
      if (!panning) return;
      panning = false;
      backdrop.style.cursor = 'grab';
      // A clean click (no pan) on the backdrop closes, like a lightbox.
      if (!moved && e.target === backdrop) this.closeImageInspector();
    };
    const onDblClick = () => fit();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.closeImageInspector();
      }
    };
    const onResize = () => fit();

    backdrop.addEventListener('wheel', onWheel, { passive: false });
    backdrop.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    backdrop.addEventListener('dblclick', onDblClick);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('resize', onResize);
    closeBtn.addEventListener('click', () => this.closeImageInspector());

    document.body.appendChild(backdrop);
    this.inspectorCleanup = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('resize', onResize);
      if (backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
    };
  }

  /** Close the fullscreen image inspector, if open. */
  private closeImageInspector(): void {
    if (this.inspectorCleanup) {
      this.inspectorCleanup();
      this.inspectorCleanup = null;
    }
  }

  /**
   * Add a debug widget to the overlay
   */
  addWidget(widget: DebugWidget): void {
    if (this.widgets.has(widget.name)) {
      console.warn(`[DebugOverlay] Widget "${widget.name}" already exists`);
      return;
    }

    this.widgets.set(widget.name, widget);
    this.contentContainer.appendChild(widget.container);
  }

  /**
   * Remove a debug widget from the overlay
   */
  removeWidget(name: string): void {
    const widget = this.widgets.get(name);
    if (widget) {
      widget.destroy();
      if (widget.container.parentNode === this.contentContainer) {
        this.contentContainer.removeChild(widget.container);
      }
      this.widgets.delete(name);
    }
  }

  /**
   * Show the debug overlay
   */
  show(): void {
    this.container.style.display = 'block';
    this.isVisible = true;

    // Start updating widgets
    if (this.updateInterval === null) {
      this.updateInterval = window.setInterval(() => {
        this.widgets.forEach((widget) => widget.update());
      }, 100); // Update every 100ms
    }
  }

  /**
   * Hide the debug overlay
   */
  hide(): void {
    this.closeImageInspector();
    this.container.style.display = 'none';
    this.isVisible = false;

    // Stop updating widgets
    if (this.updateInterval !== null) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  /**
   * Toggle the debug overlay visibility
   */
  toggle(): void {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Check if overlay is visible
   */
  get visible(): boolean {
    return this.isVisible;
  }

  /**
   * Destroy the overlay and all widgets
   */
  destroy(): void {
    this.closeImageInspector();
    if (this.updateInterval !== null) {
      clearInterval(this.updateInterval);
    }

    this.widgets.forEach((widget) => widget.destroy());
    this.widgets.clear();

    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}

/**
 * Helper function to create a widget container with common styling
 */
export function createWidgetContainer(title: string): HTMLElement {
  const container = document.createElement('div');
  container.style.cssText = `
    margin-bottom: 16px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  `;

  const titleEl = document.createElement('div');
  titleEl.textContent = title;
  titleEl.style.cssText = `
    font-weight: bold;
    margin-bottom: 8px;
    color: #4CAF50;
    font-size: 13px;
  `;

  container.appendChild(titleEl);
  return container;
}
